import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  image?: string;
  type?: 'website' | 'article';
  canonicalPath?: string;
  jsonLd?: Record<string, unknown>;
  noindex?: boolean;
}

const SITE_URL = 'https://ecoleminds.com';
const DEFAULT_IMAGE = 'https://ecoleminds.com/ecoleminds.png';

export default function SEO({
  title,
  description,
  image = DEFAULT_IMAGE,
  type = 'website',
  canonicalPath,
  jsonLd,
  noindex = false,
}: SEOProps): React.JSX.Element {
  const fullTitle = title.includes('EcoleMinds') ? title : `${title} | EcoleMinds`;
  const canonicalUrl = canonicalPath ? `${SITE_URL}${canonicalPath}` : SITE_URL;
  const imageUrl = image.startsWith('http') ? image : `${SITE_URL}${image}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && <meta name="robots" content="index, follow" />}

      {/* Canonical */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="EcoleMinds" />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {/* Article specific */}
      {type === 'article' && (
        <>
          <meta property="article:publisher" content="https://ecoleminds.com" />
        </>
      )}

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

export function createArticleJsonLd(post: {
  title: string;
  excerpt: string;
  image: string;
  author: string;
  date: string;
  slug: string;
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    image: post.image,
    author: {
      '@type': 'Person',
      name: post.author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'EcoleMinds',
      logo: {
        '@type': 'ImageObject',
        url: 'https://ecoleminds.com/ecoleminds.png',
      },
    },
    url: `https://ecoleminds.com/blog/${post.slug}`,
    datePublished: formatDate(post.date),
    dateModified: formatDate(post.date),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://ecoleminds.com/blog/${post.slug}`,
    },
  };
}

export function createWebsiteJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'EcoleMinds',
    url: 'https://ecoleminds.com',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://ecoleminds.com/blog?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };
}

function formatDate(dateStr: string): string {
  // Convert "May 15, 2026" → "2026-05-15"
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toISOString().split('T')[0];
  } catch {
    return dateStr;
  }
}
